/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import {
  Stack,
  Table,
  TableBody,
  TableContainer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useMemo } from "react";

import { QuantityChip } from "../../../components/QuantityChip";
import TestIds from "../../../testIds";
import { formatPrice } from "../../../util/helpers";
import { NotesFormReadOnly } from "../form/NotesForm";
import { SEED_MIX_TYPE, SeedMix, SeedOrdersInfo } from "../types/SeedOrderFormType";
import {
  allSeedsCount,
  allSeedWeightForPaddock,
  roundedTo,
  totalPriceForPaddock,
} from "../utils/calculateTotal";

import MixTypeIcon from "./MixTypeIcon";
import SeedFormTitle from "./SeedFormTitle";
import SeedOrderDeliveryDetails from "./SeedOrderDeliveryDetails";
import SeedOrderTableMobile from "./SeedOrderTableMobile";
import { SeedOrderTableProductRow } from "./SeedOrderTableProductRow";
import { SeedStatsLabel } from "./SeedStatsLabel";
import { SeedTableHeader } from "./SeedTableCell";

export interface SeedMixTableProps {
  seed: SeedMix;
}

export const SeedMixTable = ({ seed }: SeedMixTableProps) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const totalSeeds = useMemo(() => allSeedsCount(seed.products), [seed.products]);
  const warning = useMemo(() => {
    if (totalSeeds === 1 && seed.mixType === SEED_MIX_TYPE.CUSTOM) {
      return "This custom seed mix has only 1 product";
    }
    return undefined;
  }, [totalSeeds, seed]);

  if (isSm) {
    return <SeedOrderTableMobile seed={seed} />;
  }

  return seed.paddocks.map((paddock) => {
    const weight = allSeedWeightForPaddock(seed.products, paddock);
    const sowingRate = weight / paddock.hectares;
    const pricePerHa = totalPriceForPaddock(seed.products, paddock);
    return (
      <div key={`${seed.id}-${paddock.name}`}>
        {warning && (
          <Typography
            variant="body2"
            color={theme.colors.error500}
            mt={0.5}
            data-testid={TestIds.feat.seed.product.singleSeedInCustomSeed}
          >
            {warning}
          </Typography>
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          borderBottom={1}
          borderColor={colors.primary100}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" color={theme.colors.primary500} fontWeight="bold">
              {paddock.name}
            </Typography>
            <QuantityChip label={`${paddock.hectares} Ha`} />
            <QuantityChip label={`${allSeedsCount(seed.products)} products`} />
            {seed.mixType === SEED_MIX_TYPE.PREMIX ? null : (
              <QuantityChip label={`${roundedTo(weight)}kg`} />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            {seed.mixType === SEED_MIX_TYPE.PREMIX ? null : (
              <SeedStatsLabel title="Sowing Rate" value={`${roundedTo(sowingRate)} kg/Ha`} />
            )}
            <SeedStatsLabel title="Price per ha" value={formatPrice(pricePerHa)} />
          </Stack>
        </Stack>
        <Table aria-label="Seed orders table">
          <SeedTableHeader mixType={seed.mixType} />
          <TableBody>
            {seed.products.map((seedProduct) => (
              <SeedOrderTableProductRow
                key={`${seedProduct.lineId}=${paddock.name}`}
                mixType={seed.mixType}
                paddock={paddock}
                seedProduct={seedProduct}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    );
  });
};

export const SeedOrderTable = ({ seedOrder }: { seedOrder: SeedOrdersInfo }) => {
  return (
    <TableContainer data-testid={TestIds.feat.agreement.salesOrder.table}>
      {seedOrder?.mixes.map((seed, index) => {
        return (
          <Stack
            key={seed.id}
            bgcolor={colors.shade0}
            mt={1}
            sx={{ breakBefore: index === 0 ? "avoid" : "page" }}
          >
            <SeedFormTitle title={seed.name} icon={<MixTypeIcon type={seed.mixType} />}>
              <SeedOrderDeliveryDetails seed={seed} />
            </SeedFormTitle>
            <SeedMixTable key={seed.id} seed={seed} />
            <NotesFormReadOnly
              internalNotes={seed.internalNotes}
              externalNotes={seed.externalNotes}
            />
          </Stack>
        );
      })}
    </TableContainer>
  );
};
